<template>
  <div class="methodology-page">
    <AppHeader mobile-header-mode="back-only" />
    <article class="paper">
      <header class="paper-header">
        <p class="kicker">Methodological Monograph</p>
        <h1>BallerAlgo: A Public Methodology Paper on Football Prediction and Match Salience</h1>
      </header>

      <section class="card">
        <h2>Abstract</h2>
        <p>
          BallerAlgo approaches football prediction as a layered analytical problem rather than a
          single act of classification. Historical competition records are converted into pre-match
          competitive states, these states are evaluated through target-specific predictive procedures,
          and the resulting probabilities are then translated into publication-facing market views for
          the live product. A second ranking layer determines which fixtures should be treated as more
          salient in the user experience. The framework therefore combines prediction, interpretation,
          and presentation within one methodological system. This public paper is intended to explain
          the research logic of that systems internal blueprint from which it is operationally produced.
        </p>
      </section>

      <section class="card">
        <h2>Methodological Position</h2>
        <p>
          The central premise of the project is that football prediction is not only a forecasting
          exercise but also an ordering exercise. A system may estimate the probability of an event
          without adequately answering whether that event is strategically or editorially important to
          a visitor. BallerAlgo therefore separates the question of market likelihood from the question
          of match significance.
        </p>
        <p>
          From a scholarly standpoint, the framework is best described as an applied predictive system
          with a strong interpretability bias. It prefers coherent public reasoning, product utility,
          and chronological discipline over maximal opacity.
        </p>
      </section>

      <section class="card">
        <h2>Paper Structure</h2>
        <div class="toc">
          <a href="#architecture">1. Architectural Logic</a>
          <a href="#criteria">2. Design Criteria</a>
          <a href="#features">3. Representation Strategy</a>
          <a href="#training">4. Estimation Philosophy</a>
          <a href="#artifacts">5. Observed Performance Profile</a>
          <a href="#publication">6. Publication Layer</a>
          <a href="#ranking">7. Salience Framework</a>
          <a href="#limitations">8. Limitations And Future Work</a>
        </div>
      </section>

      <section id="architecture" class="section">
        <h2>1. Architectural Logic</h2>
        <p>
          The framework can be understood as a sequence of five conceptual layers. First, football
          competitions are assembled into a usable historical base. Second, each fixture is reframed as
          a pre-match state rather than a post-match record. Third, market-specific estimators are used
          to model different target families. Fourth, upcoming fixtures are translated into comparable
          forward-looking states. Fifth, the outputs are converted into product-facing market views and
          ranked for prominence. This staged design exists because the predictive act and the editorial
          act are analytically related but not identical.
        </p>

        <div class="pipeline">
          <article v-for="stage in data.pipelineStages" :key="stage.title" class="panel">
            <span class="index">{{ stage.index }}</span>
            <h3>{{ stage.title }}</h3>
            <p>{{ stage.description }}</p>
            <p class="stage-focus">{{ stage.focus }}</p>
          </article>
        </div>
      </section>

      <section id="criteria" class="section">
        <h2>2. Design Criteria</h2>
        <div class="criteria">
          <article class="panel">
            <h3>Chronological Integrity</h3>
            <p>
              Every predictive row is intended to reflect only information that would have been
              available before the match. This guards against retrospective leakage and preserves the
              legitimacy of the forecasting exercise.
            </p>
          </article>
          <article class="panel">
            <h3>Market Specificity</h3>
            <p>
              Different football markets do not necessarily share identical signal structure. The
              framework therefore treats them as distinguishable analytical objects rather than forcing
              them into one generic predictive narrative.
            </p>
          </article>
          <article class="panel">
            <h3>Interpretive Utility</h3>
            <p>
              Public-facing prediction systems must communicate more than raw output. They must also
              help visitors understand what matters, what is uncertain, and why one fixture is surfaced
              over another.
            </p>
          </article>
          <article class="panel">
            <h3>Product Salience</h3>
            <p>
              Match prominence is handled as its own problem. In methodological terms, BallerAlgo does
              not assume that maximum probability alone should determine maximum visibility.
            </p>
          </article>
        </div>

        <div class="chart-grid">
          <figure class="chart-card chart-card-wide">
            <figcaption>Figure 2A. Methodological Emphasis Profile</figcaption>
            <svg :viewBox="`0 0 ${methodologyAxesChart.width} ${methodologyAxesChart.height}`" role="img" aria-label="Horizontal bar chart showing the relative methodological emphasis of the framework">
              <g v-for="tick in methodologyAxesChart.xTicks" :key="`criteria-x-${tick.value}`">
                <line
                  :x1="tick.x"
                  :x2="tick.x"
                  :y1="methodologyAxesChart.margin.top"
                  :y2="methodologyAxesChart.height - methodologyAxesChart.margin.bottom"
                  class="grid-line"
                />
                <text :x="tick.x" :y="methodologyAxesChart.height - methodologyAxesChart.margin.bottom + 18" class="axis-text axis-text-center">
                  {{ tick.label }}
                </text>
              </g>

              <g v-for="row in methodologyAxesChart.rows" :key="`criteria-row-${row.name}`">
                <text :x="methodologyAxesChart.margin.left - 12" :y="row.y + 4" class="axis-text axis-text-left">
                  {{ row.name }}
                </text>
                <line
                  :x1="methodologyAxesChart.margin.left"
                  :x2="methodologyAxesChart.width - methodologyAxesChart.margin.right"
                  :y1="row.y"
                  :y2="row.y"
                  class="profile-track"
                />
                <rect
                  :x="methodologyAxesChart.margin.left"
                  :y="row.y - 9"
                  :width="row.x - methodologyAxesChart.margin.left"
                  height="18"
                  :fill="row.color"
                  rx="9"
                />
                <circle :cx="row.x" :cy="row.y" r="6" :fill="row.color" />
                <text :x="Math.min(row.x + 12, methodologyAxesChart.width - methodologyAxesChart.margin.right - 8)" :y="row.y + 4" class="point-label">
                  {{ row.scoreLabel }}
                </text>
              </g>
            </svg>
            <p class="chart-note">
              The profile makes visible that chronological legitimacy and confidence accountability are
              treated as first-order design commitments rather than secondary implementation details.
            </p>
          </figure>
        </div>
      </section>

      <section id="features" class="section">
        <h2>3. Representation Strategy</h2>
        <p>
          The representational core of the system is a rolling pre-match abstraction. Rather than
          interpreting rows as independent observations, the framework treats each team as carrying a
          moving competitive state across the season. That state is shaped by accumulated strength,
          table position, rate behavior, and recency-sensitive competitive signals. In this sense, the
          model is not merely learning from fixtures; it is learning from the structured state that
          precedes those fixtures.
        </p>
        <p>
          What
          matters is the representational philosophy: competition context, cumulative quality,
          positional pressure, normalized production, and recency-weighted momentum are all treated as
          relevant dimensions of football state.
        </p>

        <div class="feature-grid">
          <article v-for="family in data.featureFamilies" :key="family.name" class="panel">
            <h3>{{ family.name }}</h3>
            <p>{{ family.description }}</p>
            <p class="constructs">{{ family.columns }}</p>
          </article>
        </div>

        <div class="formula-grid">
          <article class="panel">
            <h3>Momentum As Weighted Evidence</h3>
            <p>
              Recent matches are not treated equally. More recent and more contextually meaningful
              fixtures exert greater influence on present interpretation than distant or low-signal
              events.
            </p>
          </article>
          <article class="panel">
            <h3>Attack And Defense As Distinct Constructs</h3>
            <p>
              The framework does not collapse attacking strength and defensive vulnerability into one
              undifferentiated summary. It preserves directional asymmetry because football outcomes
              often depend on imbalance rather than simple aggregate quality.
            </p>
          </article>
          <article class="panel">
            <h3>Table Position As Context, Not Decoration</h3>
            <p>
              Ranking information is treated as an active variable in methodological terms. It informs
              pressure, match significance, and comparative quality rather than merely serving as a
              descriptive label.
            </p>
          </article>
        </div>

        <div class="table-card">
          <h3>Publicly Emphasized Market Families</h3>
          <table>
            <thead>
              <tr>
                <th>Target</th>
                <th>Structure</th>
                <th>Public Presentation</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="target in data.supportedTargets" :key="target.name">
                <td>{{ target.name }}</td>
                <td>{{ target.type }}</td>
                <td>{{ target.expansion }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="training" class="section">
        <h2>4. Estimation Philosophy</h2>
        <p>
          The estimation layer follows a disciplined applied-ML posture. Each target family is trained
          independently, representation size is treated as a search dimension, and evaluation is
          performed under a controlled holdout protocol. This makes the system less theatrical than
          black-box "AI" claims and more methodologically legible as a repeatable experimental
          procedure.
        </p>
        <p>
          The current public description abstracts away the exact configuration of the training stack.
          What can be said without unnecessary exposure is that the framework uses a modern
          non-linear classifier family, searches across alternative compact and expanded
          representations, and persists the preferred market-specific configuration for subsequent
          inference.
        </p>

        <div class="equation">
          preferred configuration = argmax validation utility across candidate representation widths
        </div>

        <div class="chart-grid">
          <figure class="chart-card chart-card-wide">
            <figcaption>Figure 4A. Confidence-Weighted Error Geometry</figcaption>
            <svg :viewBox="`0 0 ${confidenceChart.width} ${confidenceChart.height}`" role="img" aria-label="Line chart showing measured reward for correct predictions and steeper penalty for incorrect predictions as confidence rises">
              <rect
                :x="confidenceChart.margin.left"
                :y="confidenceChart.margin.top"
                :width="confidenceChart.width - confidenceChart.margin.left - confidenceChart.margin.right"
                :height="confidenceChart.zeroY - confidenceChart.margin.top"
                class="zone-positive"
              />
              <rect
                :x="confidenceChart.margin.left"
                :y="confidenceChart.zeroY"
                :width="confidenceChart.width - confidenceChart.margin.left - confidenceChart.margin.right"
                :height="confidenceChart.height - confidenceChart.margin.bottom - confidenceChart.zeroY"
                class="zone-negative"
              />

              <g v-for="tick in confidenceChart.xTicks" :key="`conf-x-${tick.value}`">
                <line
                  :x1="tick.x"
                  :x2="tick.x"
                  :y1="confidenceChart.margin.top"
                  :y2="confidenceChart.height - confidenceChart.margin.bottom"
                  class="grid-line"
                />
                <text :x="tick.x" :y="confidenceChart.height - confidenceChart.margin.bottom + 18" class="axis-text axis-text-center">
                  {{ tick.label }}
                </text>
              </g>

              <g v-for="tick in confidenceChart.yTicks" :key="`conf-y-${tick.value}`">
                <line
                  :x1="confidenceChart.margin.left"
                  :x2="confidenceChart.width - confidenceChart.margin.right"
                  :y1="tick.y"
                  :y2="tick.y"
                  class="grid-line"
                />
                <text :x="confidenceChart.margin.left - 10" :y="tick.y + 4" class="axis-text axis-text-left">
                  {{ tick.label }}
                </text>
              </g>

              <line
                :x1="confidenceChart.margin.left"
                :x2="confidenceChart.width - confidenceChart.margin.right"
                :y1="confidenceChart.zeroY"
                :y2="confidenceChart.zeroY"
                class="axis-line"
              />
              <line
                :x1="confidenceChart.margin.left"
                :x2="confidenceChart.margin.left"
                :y1="confidenceChart.margin.top"
                :y2="confidenceChart.height - confidenceChart.margin.bottom"
                class="axis-line"
              />

              <path :d="confidenceChart.correctPath" class="curve-line curve-positive" />
              <path :d="confidenceChart.incorrectPath" class="curve-line curve-negative" />

              <g v-for="point in confidenceChart.points" :key="`conf-point-${point.confidence}`">
                <circle :cx="point.x" :cy="point.correctY" r="4.5" class="curve-marker curve-positive-fill" />
                <circle :cx="point.x" :cy="point.incorrectY" r="4.5" class="curve-marker curve-negative-fill" />
              </g>

              <text :x="confidenceChart.width / 2" :y="confidenceChart.height - 8" class="axis-title axis-text-center">
                Declared probability confidence
              </text>
              <text
                :x="20"
                :y="confidenceChart.height / 2"
                class="axis-title axis-text-center"
                transform="rotate(-90 20, 165)"
              >
                Relative utility
              </text>
            </svg>
            <div class="legend-row">
              <span class="legend-item">
                <span class="legend-swatch curve-positive-fill"></span>
                Correct prediction
              </span>
              <span class="legend-item">
                <span class="legend-swatch curve-negative-fill"></span>
                Incorrect prediction
              </span>
            </div>
            <p class="chart-note">
              The operative idea is not merely to reward being right, but to penalize being wrong in
              proportion to declared certainty. This encourages better probability discipline and a
              more trustworthy separation between strong and weak calls.
            </p>
          </figure>
        </div>

        <div class="table-card">
          <h3>Evaluation Logic</h3>
          <table>
            <thead>
              <tr>
                <th>Stage</th>
                <th>Description</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="step in data.trainingProtocol" :key="step.stage">
                <td>{{ step.stage }}</td>
                <td>{{ step.implementation }}</td>
                <td>{{ step.purpose }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="artifacts" class="section">
        <h2>5. Observed Performance Profile</h2>
        <p>
          The following profile is presented as a public summary of observed task behavior rather than
          as a complete internal experiment registry. It is intended to convey relative task
          difficulty, signal stability, and comparative market structure.
        </p>
        <div class="card probability-card">
          <h3>Probability Quality Beyond Directional Hit Rate</h3>
          <p>
            In football forecasting, some public-facing markets are structurally difficult enough that
            top-line classification accuracy can look modest when judged in isolation. That does not
            automatically imply methodological weakness. For a probability-bearing system, the more
            meaningful question is whether uncertainty is ordered well: ambiguous fixtures should carry
            diffuse probability mass, stronger cases should concentrate that mass more decisively, and
            overconfident errors should be treated as substantially more serious than cautious misses.
          </p>
          <p>
            BallerAlgo is therefore best interpreted not as a blunt win-loss classifier but as a
            confidence-sensitive probabilistic framework. Its practical value lies in how effectively
            it separates high-conviction and low-conviction situations, enabling more disciplined
            downstream ranking, display, and interpretation than raw accuracy alone would suggest.
          </p>
        </div>

        <div class="chart-grid">
          <figure class="chart-card">
            <figcaption>Figure 1. Comparative Metric Profile Across Public Market Families</figcaption>
            <svg :viewBox="`0 0 ${metricChart.width} ${metricChart.height}`" role="img" aria-label="Grouped comparison chart for accuracy, F1, and Jaccard across market families">
              <g v-for="tick in metricChart.yTicks" :key="`metric-y-${tick.value}`">
                <line
                  :x1="metricChart.margin.left"
                  :x2="metricChart.width - metricChart.margin.right"
                  :y1="tick.y"
                  :y2="tick.y"
                  class="grid-line"
                />
                <text :x="metricChart.margin.left - 10" :y="tick.y + 4" class="axis-text axis-text-left">
                  {{ tick.label }}
                </text>
              </g>

              <line
                :x1="metricChart.margin.left"
                :x2="metricChart.margin.left"
                :y1="metricChart.margin.top"
                :y2="metricChart.height - metricChart.margin.bottom"
                class="axis-line"
              />
              <line
                :x1="metricChart.margin.left"
                :x2="metricChart.width - metricChart.margin.right"
                :y1="metricChart.height - metricChart.margin.bottom"
                :y2="metricChart.height - metricChart.margin.bottom"
                class="axis-line"
              />

              <g v-for="bar in metricChart.bars" :key="`${bar.target}-${bar.series}`">
                <rect
                  :x="bar.x"
                  :y="bar.y"
                  :width="bar.width"
                  :height="bar.height"
                  :fill="bar.color"
                  rx="4"
                />
              </g>

              <g v-for="label in metricChart.labels" :key="`metric-label-${label.text}`">
                <text :x="label.x" :y="label.y" class="axis-text axis-text-center">
                  {{ label.text }}
                </text>
              </g>
            </svg>
            <div class="legend-row">
              <span v-for="series in metricSeries" :key="series.key" class="legend-item">
                <span class="legend-swatch" :style="{ backgroundColor: series.color }"></span>
                {{ series.label }}
              </span>
            </div>
          </figure>

          <figure class="chart-card">
            <figcaption>Figure 2. Task Geometry in the Accuracy-Jaccard Plane</figcaption>
            <svg :viewBox="`0 0 ${scatterChart.width} ${scatterChart.height}`" role="img" aria-label="Scatter plot showing market families by accuracy and Jaccard score">
              <g v-for="tick in scatterChart.xTicks" :key="`scatter-x-${tick.value}`">
                <line
                  :x1="tick.x"
                  :x2="tick.x"
                  :y1="scatterChart.margin.top"
                  :y2="scatterChart.height - scatterChart.margin.bottom"
                  class="grid-line"
                />
                <text :x="tick.x" :y="scatterChart.height - scatterChart.margin.bottom + 18" class="axis-text axis-text-center">
                  {{ tick.label }}
                </text>
              </g>

              <g v-for="tick in scatterChart.yTicks" :key="`scatter-y-${tick.value}`">
                <line
                  :x1="scatterChart.margin.left"
                  :x2="scatterChart.width - scatterChart.margin.right"
                  :y1="tick.y"
                  :y2="tick.y"
                  class="grid-line"
                />
                <text :x="scatterChart.margin.left - 10" :y="tick.y + 4" class="axis-text axis-text-left">
                  {{ tick.label }}
                </text>
              </g>

              <line
                :x1="scatterChart.margin.left"
                :x2="scatterChart.margin.left"
                :y1="scatterChart.margin.top"
                :y2="scatterChart.height - scatterChart.margin.bottom"
                class="axis-line"
              />
              <line
                :x1="scatterChart.margin.left"
                :x2="scatterChart.width - scatterChart.margin.right"
                :y1="scatterChart.height - scatterChart.margin.bottom"
                :y2="scatterChart.height - scatterChart.margin.bottom"
                class="axis-line"
              />

              <g v-for="point in scatterChart.points" :key="`point-${point.label}`">
                <circle :cx="point.x" :cy="point.y" :r="point.r" :fill="point.color" opacity="0.88" />
                <text :x="point.x" :y="point.y - point.r - 6" class="point-label">{{ point.label }}</text>
              </g>

              <text
                :x="scatterChart.width / 2"
                :y="scatterChart.height - 8"
                class="axis-title axis-text-center"
              >
                Accuracy
              </text>
              <text
                :x="18"
                :y="scatterChart.height / 2"
                class="axis-title axis-text-center"
                transform="rotate(-90 18, 160)"
              >
                Jaccard
              </text>
            </svg>
          </figure>
        </div>

        <div class="metrics">
          <article v-for="metric in data.artifactMetrics" :key="metric.target" class="panel">
            <div class="metric-head">
              <h3>{{ metric.target }}</h3>
              <span>{{ percent(metric.accuracy) }} accuracy</span>
            </div>
            <div class="bar-track">
              <div class="bar" :style="{ width: percent(metric.accuracy) }"></div>
            </div>
            <div class="metric-meta">
              <span>F1: {{ percent(metric.f1) }}</span>
              <span>Jaccard: {{ percent(metric.jaccard) }}</span>
            </div>
            <p class="top-features">{{ metric.profile }}</p>
          </article>
        </div>
      </section>

      <section id="publication" class="section">
        <h2>6. Publication Layer</h2>
        <p>
          The inferential output of the system is not displayed raw. It is translated into a
          publication schema intended for end-user clarity. This matters because the analytical layer
          and the product layer serve different purposes: one estimates, the other communicates.
        </p>
        <p>
          In practical terms, target-specific outcomes are reconciled into a common market vocabulary
          so that visitors encounter a coherent interface rather than a fragmented set of disconnected
          model outputs. This translation layer is also where the system exercises restraint by
          elevating only a narrower subset of more interpretable public-facing market categories.
        </p>

        <div class="chart-grid">
          <figure class="chart-card chart-card-wide">
            <figcaption>Figure 6A. Public Market Communication Matrix</figcaption>
            <svg :viewBox="`0 0 ${heatmapChart.width} ${heatmapChart.height}`" role="img" aria-label="Heatmap showing the public communication value of different market families">
              <g v-for="label in heatmapChart.columnLabels" :key="`heat-col-${label.text}`">
                <text :x="label.x" :y="label.y" class="axis-text axis-text-center heat-label">
                  {{ label.text }}
                </text>
              </g>

              <g v-for="label in heatmapChart.rowLabels" :key="`heat-row-${label.text}`">
                <text :x="label.x" :y="label.y" class="axis-text axis-text-left heat-label">
                  {{ label.text }}
                </text>
              </g>

              <g v-for="cell in heatmapChart.cells" :key="`heat-cell-${cell.row}-${cell.column}`">
                <rect
                  :x="cell.x"
                  :y="cell.y"
                  :width="cell.width"
                  :height="cell.height"
                  rx="8"
                  :fill="cell.color"
                />
                <text :x="cell.x + cell.width / 2" :y="cell.y + cell.height / 2 + 4" class="heat-cell-text axis-text-center">
                  {{ cell.value }}
                </text>
              </g>
            </svg>
            <div class="legend-row">
              <span class="legend-item">
                Communication weight:
              </span>
              <span v-for="level in heatLegend" :key="`heat-legend-${level.value}`" class="legend-item">
                <span class="legend-swatch" :style="{ backgroundColor: level.color }"></span>
                {{ level.value }}
              </span>
              <span class="legend-item legend-meta">low to high</span>
            </div>
            <p class="chart-note">
              The matrix illustrates why public emphasis converges on result, BTTS, and over/under
              2.5: they offer the strongest combination of readability, editorial salience, and
              practical probability utility.
            </p>
          </figure>
        </div>

        <div class="table-card">
          <h3>Public Market Translation</h3>
          <table>
            <thead>
              <tr>
                <th>Analytical Target</th>
                <th>Outcome Form</th>
                <th>Displayed Market Family</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mapping in data.publicationMappings" :key="`${mapping.target}-${mapping.raw}`">
                <td>{{ mapping.target }}</td>
                <td>{{ mapping.raw }}</td>
                <td>{{ mapping.published }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="panel">
          <h3>Primary Display Priorities</h3>
          <p>
            Public emphasis is deliberately restricted. This protects readability, reduces trivial
            market dominance, and keeps the product centered on categories that most visitors can
            understand quickly.
          </p>
          <div class="tags">
            <span v-for="market in data.priorityMarkets" :key="market">{{ market }}</span>
          </div>
        </div>
      </section>

      <section id="ranking" class="section">
        <h2>7. Salience Framework</h2>
        <p>
          One of the defining characteristics of BallerAlgo is that match visibility is not left to
          kickoff time or raw probability alone. Instead, the product uses a salience framework that
          asks a broader question: which matches are most deserving of attention when league status,
          table context, competitive balance, title proximity, and confidence are all considered
          together?
        </p>
        <p>
          This framework is intentionally transparent at the conceptual level and intentionally guarded
          at the exact operational level.
        </p>

        <div class="equation">
          match salience = f(competition significance, table strength, balance, title proximity, confidence)
        </div>

        <div class="chart-grid">
          <figure class="chart-card">
            <figcaption>Figure 3. Salience Composition of the Featured-Ranking Framework</figcaption>
            <div class="stacked-bar">
              <div
                v-for="segment in salienceSegments"
                :key="segment.name"
                class="stacked-segment"
                :style="{ width: `${segment.percent}%`, backgroundColor: segment.color }"
              ></div>
            </div>
            <div class="legend-block">
              <div v-for="segment in salienceSegments" :key="`legend-${segment.name}`" class="legend-item legend-item-block">
                <span class="legend-swatch" :style="{ backgroundColor: segment.color }"></span>
                <span>{{ segment.name }}</span>
                <span class="legend-meta">{{ segment.percent.toFixed(0) }}%</span>
              </div>
            </div>
          </figure>

          <figure class="chart-card">
            <figcaption>Figure 4. League Salience Ladder</figcaption>
            <svg :viewBox="`0 0 ${leagueChart.width} ${leagueChart.height}`" role="img" aria-label="League salience ladder showing relative emphasis bands">
              <g v-for="tick in leagueChart.xTicks" :key="`league-x-${tick.value}`">
                <line
                  :x1="tick.x"
                  :x2="tick.x"
                  :y1="leagueChart.margin.top"
                  :y2="leagueChart.height - leagueChart.margin.bottom"
                  class="grid-line"
                />
                <text :x="tick.x" :y="leagueChart.height - leagueChart.margin.bottom + 18" class="axis-text axis-text-center">
                  {{ tick.label }}
                </text>
              </g>

              <g v-for="league in leagueChart.rows" :key="`league-row-${league.name}`">
                <text :x="leagueChart.margin.left - 12" :y="league.y + 4" class="axis-text axis-text-left">
                  {{ league.short }}
                </text>
                <line
                  :x1="leagueChart.margin.left"
                  :x2="league.x"
                  :y1="league.y"
                  :y2="league.y"
                  class="lollipop-line"
                />
                <circle :cx="league.x" :cy="league.y" r="7" :fill="league.color" />
              </g>
            </svg>
          </figure>
        </div>

        <div class="weights">
          <article v-for="weight in data.featuredWeights" :key="weight.name" class="panel">
            <div class="metric-head">
              <h3>{{ weight.name }}</h3>
              <span>{{ weight.tier }}</span>
            </div>
            <div class="bar-track">
              <div
                class="bar weight-bar"
                :style="{ width: `${(weight.value / maxWeight) * 100}%` }"
              ></div>
            </div>
            <p>{{ weight.description }}</p>
          </article>
        </div>

        <div class="table-card">
          <h3>Illustrative League Salience Bands</h3>
          <table>
            <thead>
              <tr>
                <th>League</th>
                <th>Band</th>
                <th>Interpretive Role</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="league in data.leagueImportance" :key="league.name">
                <td>{{ league.name }}</td>
                <td>{{ league.tier }}</td>
                <td>{{ league.note }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="limitations" class="section">
        <h2>8. Limitations And Future Work</h2>
        <div class="criteria">
          <article v-for="item in data.limitations" :key="item.title" class="panel">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </article>
        </div>
        <p class="closing-note">
          In research terms, the current system should be understood as a serious applied baseline:
          more disciplined than naive ranking systems, more interpretable than fully opaque end-to-end
          architectures, and intentionally positioned for future advances in validation, calibration,
          and longitudinal robustness.
        </p>
      </section>
      <router-link to="/" class="back-button">Back</router-link>
    </article>
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";
import { methodologyData } from "@/utils/methodologyData";

export default {
  name: "MethodologyPage",
  components: {
    AppHeader,
    AppFooter,
  },
  data() {
    return {
      data: methodologyData,
    };
  },
  computed: {
    maxWeight() {
      return Math.max(...this.data.featuredWeights.map((item) => item.value));
    },
    methodologyAxesChart() {
      const width = 760;
      const height = 80 + this.data.methodologicalAxes.length * 42;
      const margin = { top: 20, right: 48, bottom: 38, left: 220 };
      const plotWidth = width - margin.left - margin.right;
      const palette = ["#173b63", "#245782", "#2f6ca3", "#4a86bd", "#77aee0", "#d1a24a"];
      const xScale = (value) => margin.left + value * plotWidth;

      const rows = this.data.methodologicalAxes.map((axis, index) => ({
        ...axis,
        x: xScale(axis.score),
        y: margin.top + 22 + index * 42,
        color: palette[index % palette.length],
        scoreLabel: `${Math.round(axis.score * 100)}`,
      }));

      const xTicks = [0, 0.25, 0.5, 0.75, 1].map((value) => ({
        value,
        label: `${Math.round(value * 100)}`,
        x: xScale(value),
      }));

      return { width, height, margin, rows, xTicks };
    },
    metricSeries() {
      return [
        { key: "accuracy", label: "Accuracy", color: "#2f6ca3" },
        { key: "f1", label: "F1", color: "#4a9d77" },
        { key: "jaccard", label: "Jaccard", color: "#d39b3a" },
      ];
    },
    confidenceChart() {
      const width = 760;
      const height = 330;
      const margin = { top: 18, right: 18, bottom: 48, left: 60 };
      const plotWidth = width - margin.left - margin.right;
      const plotHeight = height - margin.top - margin.bottom;
      const minX = 0.5;
      const maxX = 0.96;
      const minY = -1.2;
      const maxY = 0.4;

      const xScale = (value) =>
        margin.left + ((value - minX) / (maxX - minX)) * plotWidth;
      const yScale = (value) =>
        margin.top + (1 - (value - minY) / (maxY - minY)) * plotHeight;

      const points = this.data.confidenceProfile.map((row) => ({
        ...row,
        x: xScale(row.confidence),
        correctY: yScale(row.correct),
        incorrectY: yScale(row.incorrect),
      }));

      const correctPath = this.buildLinePath(
        points.map((point) => ({ x: point.x, y: point.correctY }))
      );
      const incorrectPath = this.buildLinePath(
        points.map((point) => ({ x: point.x, y: point.incorrectY }))
      );

      const xTicks = [0.5, 0.6, 0.7, 0.8, 0.9].map((value) => ({
        value,
        label: `${Math.round(value * 100)}%`,
        x: xScale(value),
      }));

      const yTicks = [-1.0, -0.5, 0, 0.25].map((value) => ({
        value,
        label: value === 0 ? "0" : value.toFixed(2),
        y: yScale(value),
      }));

      return {
        width,
        height,
        margin,
        points,
        correctPath,
        incorrectPath,
        xTicks,
        yTicks,
        zeroY: yScale(0),
      };
    },
    metricChart() {
      const width = 760;
      const height = 330;
      const margin = { top: 22, right: 18, bottom: 64, left: 52 };
      const plotWidth = width - margin.left - margin.right;
      const plotHeight = height - margin.top - margin.bottom;
      const groupWidth = plotWidth / this.data.artifactMetrics.length;
      const barGap = 6;
      const sidePadding = 12;
      const barWidth =
        (groupWidth - sidePadding * 2 - barGap * (this.metricSeries.length - 1)) /
        this.metricSeries.length;

      const bars = [];
      const labels = [];

      this.data.artifactMetrics.forEach((metric, index) => {
        const baseX = margin.left + index * groupWidth + sidePadding;

        this.metricSeries.forEach((series, seriesIndex) => {
          const value = metric[series.key];
          bars.push({
            target: metric.target,
            series: series.key,
            color: series.color,
            width: barWidth,
            height: value * plotHeight,
            x: baseX + seriesIndex * (barWidth + barGap),
            y: margin.top + (1 - value) * plotHeight,
          });
        });

        labels.push({
          text: this.shortMetricLabel(metric.target),
          x: margin.left + index * groupWidth + groupWidth / 2,
          y: height - margin.bottom + 22,
        });
      });

      const yTicks = [0, 0.25, 0.5, 0.75, 1].map((value) => ({
        value,
        label: `${Math.round(value * 100)}%`,
        y: margin.top + (1 - value) * plotHeight,
      }));

      return { width, height, margin, bars, labels, yTicks };
    },
    heatmapChart() {
      const width = 760;
      const height = 120 + this.data.marketCommunicationMatrix.length * 42;
      const margin = { top: 84, right: 18, bottom: 18, left: 180 };
      const columns = this.data.marketCommunicationColumns;
      const rows = this.data.marketCommunicationMatrix;
      const plotWidth = width - margin.left - margin.right;
      const cellWidth = plotWidth / columns.length;
      const cellHeight = 34;

      const columnLabels = columns.map((column, index) => ({
        text: column,
        x: margin.left + index * cellWidth + cellWidth / 2,
        y: 28,
      }));

      const rowLabels = rows.map((row, index) => ({
        text: row.market,
        x: margin.left - 12,
        y: margin.top + index * 42 + cellHeight / 2 + 4,
      }));

      const cells = [];

      rows.forEach((row, rowIndex) => {
        row.values.forEach((value, columnIndex) => {
          cells.push({
            row: row.market,
            column: columns[columnIndex],
            value,
            x: margin.left + columnIndex * cellWidth + 4,
            y: margin.top + rowIndex * 42,
            width: cellWidth - 8,
            height: cellHeight,
            color: this.heatColor(value),
          });
        });
      });

      return { width, height, columnLabels, rowLabels, cells };
    },
    heatLegend() {
      return [1, 2, 3, 4, 5].map((value) => ({
        value,
        color: this.heatColor(value),
      }));
    },
    scatterChart() {
      const width = 440;
      const height = 330;
      const margin = { top: 22, right: 18, bottom: 46, left: 52 };
      const plotWidth = width - margin.left - margin.right;
      const plotHeight = height - margin.top - margin.bottom;
      const minX = 0.48;
      const maxX = 0.96;
      const minY = 0.28;
      const maxY = 0.9;

      const xScale = (value) =>
        margin.left + ((value - minX) / (maxX - minX)) * plotWidth;
      const yScale = (value) =>
        margin.top + (1 - (value - minY) / (maxY - minY)) * plotHeight;

      const points = this.data.artifactMetrics.map((metric) => ({
        label: this.shortMetricLabel(metric.target),
        x: xScale(metric.accuracy),
        y: yScale(metric.jaccard),
        r: 6 + metric.f1 * 6,
        color: this.targetColor(metric.target),
      }));

      const xTicks = [0.5, 0.6, 0.7, 0.8, 0.9].map((value) => ({
        value,
        label: `${Math.round(value * 100)}%`,
        x: xScale(value),
      }));

      const yTicks = [0.3, 0.45, 0.6, 0.75, 0.9].map((value) => ({
        value,
        label: `${Math.round(value * 100)}%`,
        y: yScale(value),
      }));

      return { width, height, margin, points, xTicks, yTicks };
    },
    salienceSegments() {
      const palette = ["#204a78", "#2f6ca3", "#5f97cb", "#d1a24a", "#e0b75f", "#ead594"];
      const total = this.data.featuredWeights.reduce((sum, item) => sum + item.value, 0);
      return this.data.featuredWeights.map((item, index) => ({
        ...item,
        color: palette[index % palette.length],
        percent: (item.value / total) * 100,
      }));
    },
    leagueChart() {
      const width = 460;
      const height = 330;
      const margin = { top: 22, right: 20, bottom: 42, left: 140 };
      const plotWidth = width - margin.left - margin.right;
      const min = 0.78;
      const max = 1.0;
      const xScale = (value) =>
        margin.left + ((value - min) / (max - min)) * plotWidth;

      const palette = ["#204a78", "#2f6ca3", "#4f88b8", "#73a7d2", "#d39b3a", "#c47b30", "#8f98ad", "#5f97cb"];
      const rows = this.data.leagueImportance.map((league, index) => ({
        ...league,
        short: this.shortLeagueLabel(league.name),
        x: xScale(league.weight),
        y: margin.top + 24 + index * 30,
        color: palette[index % palette.length],
      }));

      const xTicks = [
        { value: 0.8, label: "secondary" },
        { value: 0.87, label: "elevated" },
        { value: 0.94, label: "high" },
        { value: 1.0, label: "primary" },
      ].map((tick) => ({ ...tick, x: xScale(tick.value) }));

      return { width, height, margin, rows, xTicks };
    },
  },
  methods: {
    percent(value) {
      return `${(value * 100).toFixed(1)}%`;
    },
    buildLinePath(points) {
      if (!points.length) {
        return "";
      }

      return points
        .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
        .join(" ");
    },
    heatColor(value) {
      const palette = ["#eef3f8", "#d8e5f2", "#adc8e3", "#78a6ce", "#2f6ca3"];
      return palette[Math.max(0, Math.min(palette.length - 1, value - 1))];
    },
    shortMetricLabel(label) {
      const map = {
        Result: "Result",
        "Both Teams To Score": "BTTS",
        "Over 0.5": "O0.5",
        "Over 1.5": "O1.5",
        "Over 2.5": "O2.5",
        "Over 3.5": "O3.5",
        "Over 4.5": "O4.5",
      };
      return map[label] || label;
    },
    shortLeagueLabel(label) {
      const map = {
        "English Premier League": "EPL",
        "Brazilian Serie A": "BRA A",
        "Italian Serie A": "Serie A",
        "Spanish La Liga": "La Liga",
        "German Bundesliga": "Bundesliga",
        "French Ligue 1": "Ligue 1",
        "Portuguese Primeira Liga": "Primeira",
        "Turkish Super Lig": "Super Lig",
      };
      return map[label] || label;
    },
    targetColor(label) {
      if (label === "Result") return "#b95b4a";
      if (label === "Both Teams To Score") return "#4a9d77";
      if (label === "Over 2.5") return "#2f6ca3";
      if (label === "Over 0.5" || label === "Over 1.5") return "#6fa7da";
      if (label === "Over 3.5" || label === "Over 4.5") return "#d39b3a";
      return "#7d8aa0";
    },
  },
};
</script>

<style scoped>
.methodology-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #ffffff 100%);
  overflow-x: hidden;
}

.paper {
  width: min(1080px, calc(100% - 24px));
  margin: 12px auto 28px;
  padding: 30px;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid #dbe2ea;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 18px 48px rgba(19, 34, 58, 0.08);
  color: #203246;
  font-family: Georgia, "Times New Roman", serif;
}

.back-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  margin-bottom: 18px;
  border: 1px solid #d7e1eb;
  border-radius: 999px;
  background: #f6f9fc;
  color: #173252;
  font-family: "Helvetica Neue", Arial, sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
}

.back-button:hover {
  background: #eef4fa;
}

.paper-header {
  padding-bottom: 22px;
  border-bottom: 1px solid #ffffff;
}

.kicker {
  margin: 0 0 8px;
  font-size: 0.82rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #6b7a89;
}

.paper-header h1 {
  margin: 0;
  font-size: clamp(2.1rem, 4vw, 3.2rem);
  line-height: 1.12;
  color: #16293f;
}

.subtitle,
.section p,
.card p,
.panel p,
.closing-note {
  line-height: 1.9;
  color: #415364;
}

.subtitle {
  margin: 16px 0 0;
  font-size: 1.08rem;
  max-width: 900px;
}

.meta-row,
.toc,
.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
}

.meta-row {
  margin-top: 18px;
}

.meta-row span,
.tags span {
  padding: 8px 12px;
  border-radius: 999px;
  background: #f3f7fb;
  color: #36516d;
}

.card,
.panel,
.table-card,
.equation {
  border: 1px solid #e2e8ef;
  border-radius: 16px;
  background: #fbfcfe;
}

.card,
.equation,
.table-card {
  padding: 20px;
}

.section {
  margin-top: 28px;
}

.section h2,
.card h2 {
  margin: 0 0 14px;
  color: #16314d;
}

.probability-card {
  margin-top: 18px;
}

.probability-card h3 {
  margin: 0 0 12px;
  color: #16314d;
}

.toc {
  margin-top: 14px;
}

.toc a {
  color: #204a78;
  text-decoration: none;
}

.toc a:hover {
  text-decoration: underline;
}

.pipeline,
.criteria,
.feature-grid,
.formula-grid,
.metrics,
.weights,
.chart-grid {
  display: grid;
  gap: 14px;
  margin-top: 16px;
}

.pipeline,
.criteria,
.formula-grid {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.feature-grid,
.metrics,
.weights,
.chart-grid {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.chart-card {
  padding: 18px;
  min-width: 0;
  overflow-x: auto;
  border: 1px solid #e2e8ef;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(22, 49, 77, 0.05);
}

.chart-card-wide {
  grid-column: 1 / -1;
}

.chart-card figcaption {
  margin-bottom: 14px;
  color: #16314d;
  font-weight: 600;
  line-height: 1.6;
}

.panel {
  padding: 18px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(22, 49, 77, 0.05);
}

.index {
  display: inline-flex;
  margin-bottom: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eaf1f8;
  color: #23496d;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.panel h3,
.table-card h3 {
  margin: 0 0 10px;
  color: #16314d;
}

.stage-focus,
.constructs {
  margin-top: 12px;
  color: #1e507d;
  font-style: italic;
}

.equation {
  margin-top: 16px;
  font-size: 1.05rem;
  color: #18324f;
}

.table-card {
  overflow-x: auto;
  margin-top: 18px;
}

.chart-card svg {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
}

.table-card h3 {
  padding-bottom: 4px;
}

table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;
}

thead {
  background: #f4f7fb;
}

th,
td {
  padding: 14px 16px;
  border-top: 1px solid #e6ebf0;
  text-align: left;
  vertical-align: top;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #324355;
}

th {
  color: #17314c;
}

.metric-head,
.metric-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 10px;
}

.bar-track {
  width: 100%;
  height: 12px;
  margin: 12px 0;
  overflow: hidden;
  border-radius: 999px;
  background: #e8eef5;
}

.bar {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #2d6ea6 0%, #6fb5f0 100%);
}

.weight-bar {
  background: linear-gradient(90deg, #c58a22 0%, #f1d173 100%);
}

.metric-meta span,
.top-features,
.closing-note {
  color: #5b6a7b;
}

.legend-row,
.legend-block {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin-top: 14px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #485a6d;
  font-size: 0.92rem;
}

.legend-item-block {
  justify-content: space-between;
  width: 100%;
}

.legend-swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
  flex: 0 0 auto;
}

.legend-meta {
  color: #748291;
  margin-left: auto;
}

.grid-line {
  stroke: #e2e8ef;
  stroke-width: 1;
}

.profile-track {
  stroke: #d6e0e9;
  stroke-width: 12;
  stroke-linecap: round;
}

.axis-line {
  stroke: #9eb0c2;
  stroke-width: 1.2;
}

.axis-text,
.point-label {
  fill: #55677a;
  font-size: 12px;
  font-family: Georgia, "Times New Roman", serif;
}

.axis-text-left {
  text-anchor: end;
}

.axis-text-center {
  text-anchor: middle;
}

.axis-title {
  fill: #34495e;
  font-size: 12px;
  letter-spacing: 0.04em;
}

.heat-label {
  font-size: 11px;
}

.heat-cell-text {
  fill: #16314d;
  font-size: 13px;
  font-weight: 700;
}

.point-label {
  font-size: 11px;
  font-weight: 600;
}

.zone-positive {
  fill: rgba(91, 167, 139, 0.08);
}

.zone-negative {
  fill: rgba(185, 91, 74, 0.08);
}

.curve-line {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.curve-positive {
  stroke: #4a9d77;
}

.curve-negative {
  stroke: #b95b4a;
}

.curve-marker {
  stroke: #fff;
  stroke-width: 1.5;
}

.curve-positive-fill {
  background: #4a9d77;
  fill: #4a9d77;
}

.curve-negative-fill {
  background: #b95b4a;
  fill: #b95b4a;
}

.stacked-bar {
  display: flex;
  width: 100%;
  height: 22px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf2f7;
}

.stacked-segment {
  height: 100%;
}

.lollipop-line {
  stroke: #c6d1dc;
  stroke-width: 3;
}

.chart-note {
  margin: 14px 0 0;
  color: #5b6a7b;
  line-height: 1.75;
}

.closing-note {
  margin-top: 18px;
}

@media (max-width: 700px) {
  .paper {
    width: calc(100% - 14px);
    margin: 8px auto 18px;
    padding: 14px;
    border-radius: 14px;
  }

  .back-button {
    margin-bottom: 14px;
  }

  .card,
  .equation,
  .table-card,
  .chart-card,
  .panel {
    padding: 14px;
  }

  .paper-header h1 {
    font-size: 1.55rem;
  }

  .chart-card figcaption {
    font-size: 0.95rem;
  }

  .axis-text,
  .point-label {
    font-size: 10px;
  }

  th,
  td {
    padding: 12px 10px;
    font-size: 0.9rem;
  }
}
</style>
